import type { ClipBlob, ClipPipPosition, StartClipRecordingOptions } from './ClipsApi'
import {
  isRecordingSupported,
  normalizeAudio,
  normalizeCamera,
  resolveCanvas,
  selectMimeType,
  type NormalizedCamera,
  type ResolvedPip,
} from './clipRecorderUtils'

const DEFAULT_FPS = 30
const DEFAULT_MAX_DURATION_MS = 60_000
const DEFAULT_BITRATE = 2_500_000

function pipXY(
  position: ClipPipPosition,
  containerW: number,
  containerH: number,
  pipW: number,
  pipH: number,
  margin: number,
): { x: number; y: number } {
  switch (position) {
    case 'top-left':       return { x: margin, y: margin }
    case 'top-center':     return { x: (containerW - pipW) / 2, y: margin }
    case 'top-right':      return { x: containerW - pipW - margin, y: margin }
    case 'bottom-left':    return { x: margin, y: containerH - pipH - margin }
    case 'bottom-center':  return { x: (containerW - pipW) / 2, y: containerH - pipH - margin }
    case 'bottom-right':   return { x: containerW - pipW - margin, y: containerH - pipH - margin }
    default:               return { x: (containerW - pipW) / 2, y: margin }
  }
}

interface ManualSurface {
  ctx: CanvasRenderingContext2D
  gameCanvas: HTMLCanvasElement
  width: number
  height: number
}

interface ActiveRecording {
  recorder: MediaRecorder
  chunks: Blob[]
  mimeType: string
  width: number
  height: number
  startedAt: number
  maxDurationMs: number
  tracksToStop: MediaStreamTrack[]
  audioCleanup: (() => void) | null
  /** Set in captureMode 'manual'; drives frames via captureFrame(). */
  manual: ManualSurface | null
  webcamVideo: HTMLVideoElement | null
  camera: NormalizedCamera
  capped: boolean
  stopPromise: Promise<void> | null
  onAutoStop?: () => void
  released: boolean
  /** Live camera preview DOM overlay shown to the player during recording. */
  previewOverlay: HTMLVideoElement | null
}

export class ClipRecorder {
  private active: ActiveRecording | null = null
  private gameAudioNode: AudioNode | null = null

  isRecording(): boolean {
    return this.active !== null
  }

  useGameAudio(node: AudioNode): void {
    this.gameAudioNode = node
  }

  async start(options: StartClipRecordingOptions = {}): Promise<void> {
    if (this.active) throw new Error('[clips] a recording is already in progress')
    if (!isRecordingSupported()) {
      throw new Error('[clips] recording is not supported in this environment')
    }

    const canvas = resolveCanvas(options.canvas)
    const fps = options.fps ?? DEFAULT_FPS
    const audio = normalizeAudio(options.audio)
    const useGameAudio = audio.gameAudio && this.gameAudioNode !== null
    const width = canvas.width
    const height = canvas.height
    const manualMode = options.captureMode === 'manual'
    // Validate the mime type before acquiring the mic so an unsupported-mime
    // failure never opens (and leaks) the microphone.
    const mimeType = selectMimeType(options.mimeType)

    const camera = normalizeCamera(options.camera)
    const tracksToStop: MediaStreamTrack[] = []
    let audioCleanup: (() => void) | null = null
    let previewOverlay: HTMLVideoElement | null = null
    try {
      let micStream: MediaStream | undefined
      let webcamVideo: HTMLVideoElement | null = null

      // Camera + mic in a single getUserMedia call when both are requested.
      if (camera.enabled) {
        try {
          const constraints: MediaStreamConstraints = {
            video: {
              facingMode: camera.facingMode,
              width: { ideal: 640 },
              height: { ideal: 480 },
            },
            audio: audio.microphone,
          }
          const camStream = await navigator.mediaDevices.getUserMedia(constraints)
          const camVideoTracks = camStream.getVideoTracks()
          const camAudioTracks = camStream.getAudioTracks()
          try {
            webcamVideo = document.createElement('video')
            webcamVideo.srcObject = new MediaStream(camVideoTracks)
            webcamVideo.playsInline = true
            webcamVideo.muted = true
            await webcamVideo.play()
          } catch (playErr) {
            camVideoTracks.forEach((t) => t.stop())
            camAudioTracks.forEach((t) => t.stop())
            throw new Error('webcam-setup-failed')
          }
          camVideoTracks.forEach((t) => tracksToStop.push(t))
          if (audio.microphone) {
            micStream = new MediaStream(camAudioTracks)
            micStream.getTracks().forEach((t) => tracksToStop.push(t))
          } else {
            camAudioTracks.forEach((t) => t.stop())
          }
        } catch (camErr) {
          options.onCameraUnavailable?.()
          webcamVideo = null
        }
      }

      // Mic-only fallback when camera didn't already acquire mic.
      if (audio.microphone && !micStream) {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        micStream.getTracks().forEach((t) => tracksToStop.push(t))
      }

      // When camera PiP is active, always use a composite canvas (even in auto mode).
      const needsComposite = manualMode || webcamVideo !== null
      let recordStream: MediaStream
      let manual: ManualSurface | null = null
      if (needsComposite) {
        const composite = document.createElement('canvas')
        composite.width = width
        composite.height = height
        const ctx = composite.getContext('2d')
        if (!ctx) throw new Error('[clips] failed to get 2D context for compositing')
        recordStream = composite.captureStream(fps)
        recordStream.getTracks().forEach((t) => tracksToStop.push(t))
        manual = { ctx, gameCanvas: canvas, width, height }
      } else {
        recordStream = canvas.captureStream(fps)
        recordStream.getTracks().forEach((t) => tracksToStop.push(t))
      }

      audioCleanup = this.attachAudio(recordStream, micStream, useGameAudio, tracksToStop)
      const recorder = new MediaRecorder(recordStream, {
        mimeType,
        videoBitsPerSecond: options.videoBitsPerSecond ?? DEFAULT_BITRATE,
      })
      const chunks: Blob[] = []
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data)
      }

      previewOverlay = webcamVideo
        ? this.createPreviewOverlay(webcamVideo, canvas, camera)
        : null

      this.active = {
        recorder,
        chunks,
        mimeType,
        width,
        height,
        startedAt: Date.now(),
        maxDurationMs: options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS,
        tracksToStop,
        audioCleanup,
        manual,
        webcamVideo,
        camera,
        capped: false,
        stopPromise: null,
        onAutoStop: options.onAutoStop,
        released: false,
        previewOverlay,
      }
      if (webcamVideo && !manualMode) {
        this.startCompositeLoop()
      }
      this.startMaxDurationWatcher()
      recorder.start()
    } catch (err) {
      this.active = null
      audioCleanup?.()
      tracksToStop.forEach((t) => t.stop())
      if (previewOverlay) {
        previewOverlay.srcObject = null
        previewOverlay.remove()
      }
      throw err
    }
  }

  /** Adds one mixed audio track (game + mic) or a single source track to recordStream. */
  private attachAudio(
    recordStream: MediaStream,
    micStream: MediaStream | undefined,
    useGameAudio: boolean,
    tracksToStop: MediaStreamTrack[],
  ): (() => void) | null {
    const micTracks = micStream?.getAudioTracks() ?? []
    const gameNode = useGameAudio ? this.gameAudioNode : null
    if (!gameNode && micTracks.length === 0) return null
    if (!gameNode) {
      micTracks.forEach((t) => recordStream.addTrack(t))
      return null
    }

    // Mix through the game's AudioContext into one MediaStreamAudioDestinationNode.
    // node.context is typed as BaseAudioContext; the stream factory methods live on AudioContext.
    const ctx = gameNode.context as AudioContext
    const dest = ctx.createMediaStreamDestination()
    gameNode.connect(dest)
    let micSource: MediaStreamAudioSourceNode | null = null
    if (micTracks.length > 0) {
      micSource = ctx.createMediaStreamSource(new MediaStream(micTracks))
      micSource.connect(dest)
    }
    dest.stream.getAudioTracks().forEach((t) => {
      recordStream.addTrack(t)
      tracksToStop.push(t)
    })
    return () => {
      try {
        gameNode.disconnect(dest)
      } catch {
        /* already disconnected */
      }
      if (micSource) {
        try {
          micSource.disconnect()
        } catch {
          /* already disconnected */
        }
      }
    }
  }

  /** captureMode 'manual': snapshot the game canvas into the recording surface. */
  captureFrame(): void {
    const a = this.active
    const m = a?.manual
    if (!m) return
    m.ctx.drawImage(m.gameCanvas, 0, 0, m.width, m.height)
    if (a.webcamVideo) {
      this.drawPip(m.ctx, a.webcamVideo, m.width, m.height, a.camera.pip)
    }
  }

  async stop(): Promise<ClipBlob> {
    const active = this.active
    if (!active) throw new Error('[clips] no active recording to stop')

    if (active.capped) {
      await active.stopPromise
    } else {
      await new Promise<void>((resolve) => {
        if (active.recorder.state === 'inactive') {
          resolve()
          return
        }
        active.recorder.onstop = () => resolve()
        active.recorder.stop()
      })
      this.release(active)
    }

    const blob = new Blob(active.chunks, { type: active.mimeType })
    const durationMs = active.capped
      ? active.maxDurationMs
      : Date.now() - active.startedAt
    this.active = null

    return {
      blob,
      mimeType: active.mimeType,
      durationMs,
      width: active.width,
      height: active.height,
      sizeBytes: blob.size,
    }
  }

  cancel(): void {
    const active = this.active
    if (!active) return
    try {
      active.recorder.ondataavailable = null
      if (active.recorder.state !== 'inactive') active.recorder.stop()
    } finally {
      this.release(active)
      this.active = null
    }
  }

  /** Ends capture (stops recorder + releases mic/audio) but RETAINS chunks for stop(). */
  private cap(): void {
    const active = this.active
    if (!active || active.capped) return
    active.capped = true
    active.stopPromise = new Promise<void>((resolve) => {
      if (active.recorder.state === 'inactive') {
        this.release(active)
        resolve()
        return
      }
      active.recorder.onstop = () => {
        this.release(active)
        resolve()
      }
      active.recorder.stop()
    })
    active.onAutoStop?.()
  }

  private startMaxDurationWatcher(): void {
    const raf =
      globalThis.requestAnimationFrame?.bind(globalThis) ??
      ((cb: FrameRequestCallback) =>
        setTimeout(() => cb(Date.now()), 16) as unknown as number)
    const tick = (): void => {
      const a = this.active
      if (!a || a.capped) return
      if (Date.now() - a.startedAt >= a.maxDurationMs) {
        this.cap()
        return
      }
      raf(tick)
    }
    raf(tick)
  }

  /** Auto-mode composite loop: draws game canvas + PiP each frame. */
  private startCompositeLoop(): void {
    const raf =
      globalThis.requestAnimationFrame?.bind(globalThis) ??
      ((cb: FrameRequestCallback) =>
        setTimeout(() => cb(Date.now()), 16) as unknown as number)
    const tick = (): void => {
      const a = this.active
      if (!a || a.capped || !a.manual || !a.webcamVideo) return
      a.manual.ctx.drawImage(a.manual.gameCanvas, 0, 0, a.manual.width, a.manual.height)
      this.drawPip(a.manual.ctx, a.webcamVideo, a.manual.width, a.manual.height, a.camera.pip)
      raf(tick)
    }
    raf(tick)
  }

  private drawPip(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    canvasWidth: number,
    canvasHeight: number,
    pip: ResolvedPip,
  ): void {
    if (video.videoWidth === 0 || video.videoHeight === 0) return
    const pipW = Math.round(canvasWidth * pip.widthFraction)
    const pipH = Math.round(pipW * (video.videoHeight / Math.max(1, video.videoWidth)))
    const m = pip.margin
    const { x, y } = pipXY(pip.position, canvasWidth, canvasHeight, pipW, pipH, m)
    ctx.save()
    this.drawRoundRect(ctx, x, y, pipW, pipH, pip.borderRadius)
    ctx.clip()
    if (pip.mirror) {
      ctx.translate(x + pipW, y)
      ctx.scale(-1, 1)
      ctx.drawImage(video, 0, 0, pipW, pipH)
    } else {
      ctx.drawImage(video, x, y, pipW, pipH)
    }
    ctx.restore()
  }

  private drawRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
  ): void {
    const radius = Math.min(r, w / 2, h / 2)
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.arcTo(x + w, y, x + w, y + h, radius)
    ctx.arcTo(x + w, y + h, x, y + h, radius)
    ctx.arcTo(x, y + h, x, y, radius)
    ctx.arcTo(x, y, x + w, y, radius)
    ctx.closePath()
  }

  /**
   * Creates a fixed-position live camera preview visible at the top of the viewport.
   * Appended to document.body so no app element can hide or re-layer it.
   */
  private createPreviewOverlay(
    webcamVideo: HTMLVideoElement,
    _gameCanvas: HTMLCanvasElement,
    camera: NormalizedCamera,
  ): HTMLVideoElement {
    const preview = document.createElement('video')
    preview.srcObject = webcamVideo.srcObject
    preview.playsInline = true
    preview.muted = true
    preview.autoplay = true

    const pip = camera.pip
    const marginPx = `${pip.margin}px`
    const s = preview.style
    s.position = 'fixed'
    s.width = `${Math.round(pip.widthFraction * 100)}vw`
    s.height = 'auto'
    s.borderRadius = `${pip.borderRadius}px`
    s.objectFit = 'cover'
    s.pointerEvents = 'none'
    s.zIndex = '2147483647'

    const transforms: string[] = []

    switch (pip.position) {
      case 'top-left':
        s.top = marginPx; s.left = marginPx; break
      case 'top-center':
        s.top = marginPx; s.left = '50%'; transforms.push('translateX(-50%)'); break
      case 'top-right':
        s.top = marginPx; s.right = marginPx; break
      case 'bottom-left':
        s.bottom = marginPx; s.left = marginPx; break
      case 'bottom-center':
        s.bottom = marginPx; s.left = '50%'; transforms.push('translateX(-50%)'); break
      case 'bottom-right':
        s.bottom = marginPx; s.right = marginPx; break
    }

    if (pip.mirror) transforms.push('scaleX(-1)')
    if (transforms.length > 0) s.transform = transforms.join(' ')

    document.body.appendChild(preview)
    preview.play().catch(() => { /* autoplay policy — preview is best-effort visual */ })
    return preview
  }

  /** Idempotent: stops mic/canvas/webcam tracks, disconnects audio nodes, removes overlay. */
  private release(active: ActiveRecording): void {
    if (active.released) return
    active.released = true
    active.audioCleanup?.()
    active.tracksToStop.forEach((t) => t.stop())
    if (active.previewOverlay) {
      active.previewOverlay.srcObject = null
      active.previewOverlay.remove()
      active.previewOverlay = null
    }
    if (active.webcamVideo) {
      active.webcamVideo.srcObject = null
      active.webcamVideo = null
    }
  }
}
